import { Component, OnInit } from "@angular/core";
import { ProductService } from "../../services/product/product.service";
import { Images, ProductAttributes } from "@models/product.model";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ReviewStatus } from "@models/admin.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { S3UploadService } from "../../services/s3-upoad/s3-upload.service";
import { S3UploadCategoryName, S3UploadFileType } from "@models/fileUpload.model";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { OrderService } from "../../services/order/order.service";
import { OrderAttributes } from "@models/order.model";
import { OrderItemAttributes } from "@models/orderItem.model";
import { BaseReview } from "@models/review.model";
import { UserService } from "../../services/user/user.service";
import { finalize } from "rxjs";

@Component({
    selector: "app-rating-and-review",
    templateUrl: "./rating-and-review.component.html",
    styleUrls: ["./rating-and-review.component.scss"],
    standalone: false,
})
export class RatingAndReviewComponent implements OnInit {
    public ratingAndReview: FormGroup;
    public images: Images = { imagesUrl: [] };
    public isLoading: boolean = false;
    public stars: number[] = [1, 2, 3, 4, 5];
    public orderId!: string;
    public orderItemId!: number;
    public order!: OrderAttributes;
    public orderItem?: OrderItemAttributes;
    public isFinsihedRating: boolean = false;

    constructor(
        public productService: ProductService,
        public fromBuilder: FormBuilder,
        private snackBar: MatSnackBar,
        private s3UploadService: S3UploadService,
        private location: Location,
        private route: ActivatedRoute,
        private orderService: OrderService,
        private router: Router,
    ) {
        this.ratingAndReview = this.fromBuilder.group({
            review: [""],
            rating: ["", Validators.required],
        });
    }

    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            this.orderId = params["id"];
            this.orderItemId = +params["itemId"];
        });

        if (this.orderId) {
            this.loadOrderDetails();
        }
    }

    private loadOrderDetails(): void {
        this.isLoading = true;
        this.orderService
            .getOrderById(this.orderId)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.order = response;
                        this.orderItem = this.order.orderItems?.find((item) => item.id === this.orderItemId);
                    }
                },
            });
    }

    // upload image
    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files) return;

        const files = Array.from(input.files);
        const newImages = files.slice(0, 5 - this.images.imagesUrl.length);

        newImages.forEach((file) => {
            const fileUrl = URL.createObjectURL(file);
            if (!this.images.imagesUrl.includes(fileUrl)) {
                this.images.imagesUrl.push(fileUrl);
            }
        });
        input.value = "";
    }

    public removeImage(index: number): void {
        this.images.imagesUrl.splice(index, 1);
        const input = document.querySelector('input[type="file"]') as HTMLInputElement;
        input.value = "";
    }

    public async submitReview(): Promise<void> {
        const reviewValue = this.ratingAndReview.get("review")?.value.trim();
        const reviewStatus: string = reviewValue !== "" ? ReviewStatus.Pending : ReviewStatus.Approved;
        let s3ImageUrls: string[] = [];
        try {
            this.isLoading = true;
            if (this.images.imagesUrl.length) {
                const imageFiles = await Promise.all(this.images.imagesUrl.map(async (url) => this.urlToFile(url)));
                s3ImageUrls = await this.s3UploadService.multipartOperation(
                    imageFiles,
                    S3UploadCategoryName.RatingAndReview,
                    S3UploadFileType.Images,
                );
            }
            this.images.imagesUrl = s3ImageUrls;

            let reviewValues: BaseReview = {
                productId: this.orderItem?.productId!,
                orderId: this.order?.id!,
                rating: this.ratingAndReview.get("rating")?.value,
                review: reviewValue,
                status: reviewStatus,
                name: this.order.customerName!,
                images: this.images,
            };

            this.productService.addReview(reviewValues).subscribe({
                next: (response) => {
                    this.snackBar.open(response.message, SnackBarConfig.Actions.OKAY, {
                        duration: SnackBarConfig.Duration.MEDIUM,
                    });
                    this.isLoading = false;
                    this.images = { imagesUrl: [] };
                    this.ratingAndReview.reset();
                    this.navigateToReviewSuccess(this.orderItem?.productId!);
                },
                error: (error) => {
                    this.isLoading = false;
                    this.snackBar.open(error.message, SnackBarConfig.Actions.OKAY, {
                        duration: SnackBarConfig.Duration.MEDIUM,
                    });
                },
            });
        } catch (error) {
            this.isLoading = false;
        }
    }

    private urlToFile(url: string): Promise<File> {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then((response) => response.blob())
                .then((blob) => {
                    const file = new File([blob], url.split("/").pop() || "image.jpg");
                    resolve(file);
                })
                .catch(reject);
        });
    }

    public selectRating(rating: number): void {
        const currentRating = this.ratingAndReview.get("rating")?.value;
        if (currentRating === rating) {
            this.ratingAndReview.get("rating")?.setValue(null);
        } else {
            this.ratingAndReview.get("rating")?.setValue(rating);
        }
    }

    public goBack(): void {
        this.location.back();
    }

    public navigateToReviewSuccess(productId: number): void {
        this.router.navigate([`/account/orders/reviewSucccess/${productId}`]);
    }
}
